import { collection, documentId, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { CriteriaGroup, CriteriaGroupType, CriteriaIndicator } from '@/features/PerformanceCriteria/services/criteriaService';
import type { EvaluationScale } from '@/features/PerformanceCriteria/services/scalesService';
import {
    getEvaluationThresholdConfig,
    listEvaluationThresholdConfigs,
    type EvaluationAxisThresholds,
    type EvaluationThresholdConfig,
} from './evaluationThresholds';
import {
    EvaluationCollaborator,
    EvaluationInviteType,
    PerformanceEvaluationCampaign,
    PerformanceEvaluationResponse,
    RESPONSE_COLLECTION,
    getPerformanceEvaluationCampaignById,
    getPerformanceEvaluationCollaborators,
} from './evaluationsService';

type SeniorityKey = 'junior' | 'pleno' | 'senior';

export interface EvaluationNumericSummary {
    score: number;
    maxScore: number;
    count: number;
    average: number | null;
    percentage: number | null;
}

export interface EvaluationScoreAggregate {
    byType: Record<EvaluationInviteType, EvaluationNumericSummary>;
    mean: EvaluationNumericSummary;
}

export interface EvaluationResultComment {
    type: EvaluationInviteType;
    indicatorId: string;
    text: string;
}

export interface EvaluationResultIndicator {
    id: string;
    description: string;
    question: string;
    maxScore: number;
    answers: Partial<Record<EvaluationInviteType, number>>;
    average: number | null;
    percentage: number | null;
    gapSelfEvaluator: number | null;
    gapEvaluatorType: EvaluationInviteType | null;
    comments: EvaluationResultComment[];
}

export interface EvaluationResultBehavior {
    id: string;
    description: string;
    indicators: EvaluationResultIndicator[];
    aggregate: EvaluationScoreAggregate;
    gapSelfEvaluator: number | null;
    gapEvaluatorType: EvaluationInviteType | null;
}

export interface EvaluationResultGroup {
    id: string;
    description: string;
    type: CriteriaGroupType;
    behaviors: EvaluationResultBehavior[];
    aggregate: EvaluationScoreAggregate;
    expectedPercentage: number | null;
    belowExpected: boolean;
}

export interface EvaluationExpectedSeniorityComparison {
    seniorityId: string;
    seniorityName: string;
    thresholds: EvaluationAxisThresholds;
}

export interface EvaluationResultEvaluatorStatus {
    type: EvaluationInviteType;
    label: string;
    completed: boolean;
    responseId?: string;
    completedAt?: Date | null;
}

export interface EvaluationResultCollaborator extends EvaluationCollaborator {
    positionId?: string | null;
    positionName?: string | null;
    seniorityId?: string | null;
    seniorityName?: string | null;
    departmentName?: string | null;
    clientName?: string | null;
    projectName?: string | null;
    resources?: EvaluationResultCollaboratorResource[] | null;
}

export interface EvaluationResultCollaboratorResource {
    seniorityId?: string | null;
    seniorityName?: string | null;
    isPrimary?: boolean | null;
    [key: string]: unknown;
}

export interface EvaluationResultAllocation {
    clientName?: string | null;
    departmentName?: string | null;
    endDate?: unknown;
    projectName?: string | null;
    startDate?: unknown;
    status?: string | null;
    [key: string]: unknown;
}

export interface EvaluationCollaboratorResult {
    campaign: PerformanceEvaluationCampaign;
    collaborator: EvaluationResultCollaborator;
    groups: EvaluationResultGroup[];
    expectedComparisons: EvaluationExpectedSeniorityComparison[];
    overall: EvaluationScoreAggregate;
    evaluatorStatuses: EvaluationResultEvaluatorStatus[];
    completedResponses: Partial<Record<EvaluationInviteType, PerformanceEvaluationResponse>>;
    lowScoreIndicators: EvaluationResultIndicator[];
    largeGapIndicators: EvaluationResultIndicator[];
    comments: EvaluationResultComment[];
}

const EVALUATOR_TYPES: EvaluationInviteType[] = ['SELF', 'LEADER', 'CLIENT'];

const evaluatorLabels: Record<EvaluationInviteType, string> = {
    SELF: 'Autoavaliação',
    LEADER: 'Líder',
    CLIENT: 'Cliente',
};

const expectedByGroupAndSeniority: Record<string, Record<SeniorityKey, number>> = {
    AGIR: {
        junior: 0.4,
        pleno: 0.71,
        senior: 0.95,
    },
    CUIDAR: {
        junior: 0.3,
        pleno: 0.51,
        senior: 0.9,
    },
    INOVAR: {
        junior: 0.4,
        pleno: 0.71,
        senior: 0.95,
    },
    INFLUENCIAR: {
        junior: 0.2,
        pleno: 0.51,
        senior: 0.95,
    },
};

function toDate(value: unknown): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
        return (value as { toDate: () => Date }).toDate();
    }
    return null;
}

function responseTimestamp(response: PerformanceEvaluationResponse): number {
    return toDate(response.updatedAt)?.getTime()
        ?? toDate(response.createdAt)?.getTime()
        ?? 0;
}

function pickLatestCompletedResponses(
    responses: PerformanceEvaluationResponse[]
): Partial<Record<EvaluationInviteType, PerformanceEvaluationResponse>> {
    return responses
        .filter((response) => response.status === 'completed')
        .reduce<Partial<Record<EvaluationInviteType, PerformanceEvaluationResponse>>>((acc, response) => {
            const current = acc[response.type];
            if (!current || responseTimestamp(response) > responseTimestamp(current)) {
                acc[response.type] = response;
            }
            return acc;
        }, {});
}

function normalizeText(value?: string | null): string {
    return (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toUpperCase();
}

function readString(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

function readResourceList(value: unknown): EvaluationResultCollaboratorResource[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is EvaluationResultCollaboratorResource => (
        typeof item === 'object'
        && item !== null
    ));
}

function getPrimaryResource(
    resources: EvaluationResultCollaboratorResource[]
): EvaluationResultCollaboratorResource | null {
    if (resources.length === 0) {
        return null;
    }

    return resources.find((resource) => Boolean(resource.isPrimary)) ?? resources[0] ?? null;
}

function resolveResourceSeniorityName(
    resource: EvaluationResultCollaboratorResource | null,
    seniorityNameById?: ReadonlyMap<string, string>
): string | null {
    if (!resource) {
        return null;
    }

    const seniorityName = readString(resource.seniorityName);
    if (seniorityName) {
        return seniorityName;
    }

    const seniorityId = readString(resource.seniorityId);
    if (!seniorityId) {
        return null;
    }

    return seniorityNameById?.get(seniorityId) ?? seniorityId;
}

export function resolveEvaluationCollaboratorSeniorityName(params: {
    collaborator?: Partial<EvaluationResultCollaborator> | null;
    profile?: Record<string, unknown> | null;
    seniorityNameById?: ReadonlyMap<string, string>;
}): string | null {
    const { collaborator, profile, seniorityNameById } = params;
    const explicitName = readString(collaborator?.seniorityName) ?? readString(profile?.seniorityName);

    if (explicitName) {
        return explicitName;
    }

    const collaboratorPrimaryResource = getPrimaryResource(readResourceList(collaborator?.resources));
    const profilePrimaryResource = getPrimaryResource(readResourceList(profile?.resources));

    return resolveResourceSeniorityName(collaboratorPrimaryResource, seniorityNameById)
        ?? resolveResourceSeniorityName(profilePrimaryResource, seniorityNameById);
}

function normalizeSeniority(value?: string | null): SeniorityKey | null {
    const normalized = normalizeText(value);
    if (normalized.includes('JUNIOR')) return 'junior';
    if (normalized.includes('PLENO')) return 'pleno';
    if (normalized.includes('SENIOR')) return 'senior';
    return null;
}

function normalizeThresholdValue(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1
        ? value
        : null;
}

function getConfiguredExpectedPercentage(
    groupDescription: string,
    expectedThresholds?: EvaluationAxisThresholds | null
): number | null {
    if (!expectedThresholds) {
        return null;
    }

    const groupKey = normalizeText(groupDescription);
    const match = Object.entries(expectedThresholds)
        .find(([axis]) => normalizeText(axis) === groupKey);

    return match ? normalizeThresholdValue(match[1]) : null;
}

function getExpectedPercentage(
    groupDescription: string,
    seniorityName?: string | null,
    expectedThresholds?: EvaluationAxisThresholds | null
): number | null {
    const configuredExpectedPercentage = getConfiguredExpectedPercentage(groupDescription, expectedThresholds);
    if (configuredExpectedPercentage !== null) {
        return configuredExpectedPercentage;
    }

    const groupKey = normalizeText(groupDescription);
    const seniorityKey = normalizeSeniority(seniorityName);

    if (!seniorityKey) return null;

    return expectedByGroupAndSeniority[groupKey]?.[seniorityKey] ?? null;
}

function isStandardCriteriaGroup(group: Pick<CriteriaGroup, 'type'> | EvaluationResultGroup): boolean {
    return !group.type || group.type === 'STANDARD';
}

function getMaxScore(indicator: CriteriaIndicator, scales: EvaluationScale[]): number {
    const scale = scales.find((item) => item.id === indicator.scaleId) ?? scales[0];
    const values = scale?.options
        ?.map((option) => option.value)
        .filter((value) => Number.isFinite(value)) ?? [];
    const max = values.length ? Math.max(...values) : 5;
    return max > 0 ? max : 5;
}

function average(values: Array<number | null | undefined>): number | null {
    const validValues = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    if (validValues.length === 0) return null;
    return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
}

function createEmptyNumericSummary(): EvaluationNumericSummary {
    return {
        score: 0,
        maxScore: 0,
        count: 0,
        average: null,
        percentage: null,
    };
}

function finalizeNumericSummary(summary: EvaluationNumericSummary): EvaluationNumericSummary {
    return {
        ...summary,
        average: summary.count > 0 ? summary.score / summary.count : null,
        percentage: summary.maxScore > 0 ? summary.score / summary.maxScore : null,
    };
}

function createEmptyAggregate(): EvaluationScoreAggregate {
    return {
        byType: {
            SELF: createEmptyNumericSummary(),
            LEADER: createEmptyNumericSummary(),
            CLIENT: createEmptyNumericSummary(),
        },
        mean: createEmptyNumericSummary(),
    };
}

function aggregateIndicators(indicators: EvaluationResultIndicator[]): EvaluationScoreAggregate {
    const aggregate = createEmptyAggregate();

    indicators.forEach((indicator) => {
        EVALUATOR_TYPES.forEach((type) => {
            const score = indicator.answers[type];
            if (typeof score !== 'number' || !Number.isFinite(score)) {
                return;
            }

            aggregate.byType[type].score += score;
            aggregate.byType[type].maxScore += indicator.maxScore;
            aggregate.byType[type].count += 1;
        });

        if (typeof indicator.average === 'number' && Number.isFinite(indicator.average)) {
            aggregate.mean.score += indicator.average;
            aggregate.mean.maxScore += indicator.maxScore;
            aggregate.mean.count += 1;
        }
    });

    return {
        byType: {
            SELF: finalizeNumericSummary(aggregate.byType.SELF),
            LEADER: finalizeNumericSummary(aggregate.byType.LEADER),
            CLIENT: finalizeNumericSummary(aggregate.byType.CLIENT),
        },
        mean: finalizeNumericSummary(aggregate.mean),
    };
}

function extractIndicatorComments(
    indicatorId: string,
    responsesByType: Partial<Record<EvaluationInviteType, PerformanceEvaluationResponse>>
): EvaluationResultComment[] {
    return EVALUATOR_TYPES.flatMap((type) => {
        const text = responsesByType[type]?.comments?.[indicatorId]?.trim();
        if (!text) return [];
        return [{
            type,
            indicatorId,
            text,
        }];
    });
}

// The gap compares the self-evaluation against the external evaluation. The leader is the
// default counterpart; when a campaign sends the invite to the client instead, the client
// response is the only external one, so it takes the leader's place in the comparison.
function resolveGapEvaluator(
    selfScore: number | null | undefined,
    scoresByType: Partial<Record<EvaluationInviteType, number | null>>
): { gapSelfEvaluator: number | null; gapEvaluatorType: EvaluationInviteType | null } {
    if (typeof selfScore !== 'number') {
        return { gapSelfEvaluator: null, gapEvaluatorType: null };
    }

    const gapEvaluatorType = (['LEADER', 'CLIENT'] as const)
        .find((type) => typeof scoresByType[type] === 'number') ?? null;

    return {
        gapSelfEvaluator: gapEvaluatorType !== null
            ? (scoresByType[gapEvaluatorType] as number) - selfScore
            : null,
        gapEvaluatorType,
    };
}

function buildIndicatorResult(params: {
    indicator: CriteriaIndicator;
    campaign: PerformanceEvaluationCampaign;
    responsesByType: Partial<Record<EvaluationInviteType, PerformanceEvaluationResponse>>;
}): EvaluationResultIndicator {
    const { campaign, indicator, responsesByType } = params;
    const answers = EVALUATOR_TYPES.reduce<Partial<Record<EvaluationInviteType, number>>>((acc, type) => {
        const value = responsesByType[type]?.answers?.[indicator.id];
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            acc[type] = value;
        }
        return acc;
    }, {});
    const indicatorAverage = average(EVALUATOR_TYPES.map((type) => answers[type]));
    const maxScore = getMaxScore(indicator, campaign.scalesSnapshot);

    return {
        id: indicator.id,
        description: indicator.description,
        question: indicator.leaderEvaluationQuestion || indicator.selfEvaluationQuestion || indicator.description,
        maxScore,
        answers,
        average: indicatorAverage,
        percentage: indicatorAverage !== null ? indicatorAverage / maxScore : null,
        ...resolveGapEvaluator(answers.SELF, answers),
        comments: extractIndicatorComments(indicator.id, responsesByType),
    };
}

function sortCriteriaGroups(groups: CriteriaGroup[]): CriteriaGroup[] {
    const order = (type?: CriteriaGroup['type']) => {
        if (type === 'PROLOGUE') return 0;
        if (type === 'EPILOGUE') return 2;
        return 1;
    };

    return [...groups].sort((left, right) => order(left.type) - order(right.type));
}

export function buildEvaluationCollaboratorResult(params: {
    campaign: PerformanceEvaluationCampaign;
    collaborator: EvaluationResultCollaborator;
    responses: PerformanceEvaluationResponse[];
    expectedThresholds?: EvaluationAxisThresholds | null;
    expectedComparisons?: EvaluationExpectedSeniorityComparison[];
}): EvaluationCollaboratorResult {
    const { campaign, collaborator, responses, expectedThresholds, expectedComparisons = [] } = params;
    const completedResponses = pickLatestCompletedResponses(responses);

    const groups = sortCriteriaGroups(campaign.criteriaSnapshot)
        .map((group, groupIndex) => {
            const behaviors = group.behaviors.map((behavior) => {
                const indicators = behavior.indicators
                    .filter((indicator) => indicator.isActive)
                    .map((indicator) => buildIndicatorResult({
                        indicator,
                        campaign,
                        responsesByType: completedResponses,
                    }));

                const aggregate = aggregateIndicators(indicators);

                return {
                    id: behavior.id,
                    description: behavior.description,
                    indicators,
                    aggregate,
                    ...resolveGapEvaluator(aggregate.byType.SELF.average, {
                        LEADER: aggregate.byType.LEADER.average,
                        CLIENT: aggregate.byType.CLIENT.average,
                    }),
                };
            }).filter((behavior) => behavior.indicators.length > 0);
            const groupIndicators = behaviors.flatMap((behavior) => behavior.indicators);
            const aggregate = aggregateIndicators(groupIndicators);
            const expectedPercentage = getExpectedPercentage(group.description, collaborator.seniorityName, expectedThresholds);

            return {
                id: group.id ?? `${group.description}-${groupIndex}`,
                description: group.description,
                type: group.type ?? 'STANDARD',
                behaviors,
                aggregate: isStandardCriteriaGroup(group) ? aggregate : createEmptyAggregate(),
                expectedPercentage: isStandardCriteriaGroup(group) ? expectedPercentage : null,
                belowExpected: expectedPercentage !== null
                    && aggregate.mean.percentage !== null
                    && isStandardCriteriaGroup(group)
                    && aggregate.mean.percentage < expectedPercentage,
            };
        })
        .filter((group) => group.behaviors.length > 0);
    const scoreGroups = groups.filter(isStandardCriteriaGroup);
    const scoreIndicators = scoreGroups.flatMap((group) => group.behaviors.flatMap((behavior) => behavior.indicators));
    const indicators = groups.flatMap((group) => group.behaviors.flatMap((behavior) => behavior.indicators));
    const overall = aggregateIndicators(scoreIndicators);
    const lowScoreIndicators = [...scoreIndicators]
        .filter((indicator) => indicator.percentage !== null)
        .sort((left, right) => (left.percentage ?? 0) - (right.percentage ?? 0))
        .slice(0, 5);
    const largeGapIndicators = [...scoreIndicators]
        .filter((indicator) => indicator.gapSelfEvaluator !== null)
        .sort((left, right) => Math.abs(right.gapSelfEvaluator ?? 0) - Math.abs(left.gapSelfEvaluator ?? 0))
        .slice(0, 5);
    const comments = indicators.flatMap((indicator) => indicator.comments);
    const evaluatorStatuses = EVALUATOR_TYPES.map((type) => {
        const response = completedResponses[type];
        return {
            type,
            label: evaluatorLabels[type],
            completed: Boolean(response),
            responseId: response?.id,
            completedAt: response ? toDate(response.updatedAt) ?? toDate(response.createdAt) : null,
        };
    });

    return {
        campaign,
        collaborator,
        groups,
        expectedComparisons,
        overall,
        evaluatorStatuses,
        completedResponses,
        lowScoreIndicators,
        largeGapIndicators,
        comments,
    };
}

async function getPublicCollaboratorProfiles(ids: string[]): Promise<Map<string, Record<string, unknown>>> {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    const profileById = new Map<string, Record<string, unknown>>();

    for (let index = 0; index < uniqueIds.length; index += 10) {
        const chunk = uniqueIds.slice(index, index + 10);
        const snapshot = await getDocs(query(
            collection(db, 'collaborators_public'),
            where(documentId(), 'in', chunk)
        ));
        snapshot.docs.forEach((profileDoc) => {
            profileById.set(profileDoc.id, profileDoc.data());
        });
    }

    return profileById;
}

function collectSeniorityIds(...sources: Array<Record<string, unknown> | Partial<EvaluationResultCollaborator> | undefined>): string[] {
    const ids = new Set<string>();

    sources.forEach((source) => {
        const sourceSeniorityId = readString(source?.seniorityId);
        if (sourceSeniorityId) {
            ids.add(sourceSeniorityId);
        }

        readResourceList(source?.resources).forEach((resource) => {
            const resourceSeniorityId = readString(resource.seniorityId);
            if (resourceSeniorityId) {
                ids.add(resourceSeniorityId);
            }
        });
    });

    return Array.from(ids);
}

async function getSeniorityNamesById(ids: string[]): Promise<Map<string, string>> {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    const seniorityNameById = new Map<string, string>();

    for (let index = 0; index < uniqueIds.length; index += 10) {
        const chunk = uniqueIds.slice(index, index + 10);
        const snapshot = await getDocs(query(
            collection(db, 'seniorities'),
            where(documentId(), 'in', chunk)
        ));
        snapshot.docs.forEach((seniorityDoc) => {
            const name = readString(seniorityDoc.data().name);
            if (name) {
                seniorityNameById.set(seniorityDoc.id, name);
            }
        });
    }

    return seniorityNameById;
}

function readAllocationList(value: unknown): EvaluationResultAllocation[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is EvaluationResultAllocation => (
        typeof item === 'object'
        && item !== null
    ));
}

function allocationTimestamp(value: unknown): number {
    return toDate(value)?.getTime() ?? 0;
}

function isActiveAllocation(allocation: EvaluationResultAllocation, referenceDate: Date): boolean {
    const status = readString(allocation.status) ?? 'Active';
    if (status !== 'Active') {
        return false;
    }

    const startDate = toDate(allocation.startDate);
    const endDate = toDate(allocation.endDate);
    const referenceTime = referenceDate.getTime();

    if (startDate && startDate.getTime() > referenceTime) {
        return false;
    }

    if (endDate && endDate.getTime() < referenceTime) {
        return false;
    }

    return Boolean(readString(allocation.clientName) || readString(allocation.projectName));
}

export function pickEvaluationResultAllocation(
    allocations: EvaluationResultAllocation[],
    referenceDate: Date = new Date()
): EvaluationResultAllocation | null {
    const withClientOrProject = allocations.filter((allocation) => (
        readString(allocation.clientName)
        || readString(allocation.projectName)
    ));
    const activeAllocation = withClientOrProject
        .filter((allocation) => isActiveAllocation(allocation, referenceDate))
        .sort((left, right) => allocationTimestamp(right.startDate) - allocationTimestamp(left.startDate))[0];

    if (activeAllocation) {
        return activeAllocation;
    }

    return withClientOrProject
        .sort((left, right) => allocationTimestamp(right.startDate) - allocationTimestamp(left.startDate))[0] ?? null;
}

async function getCollaboratorAllocationDetails(collaboratorId: string): Promise<EvaluationResultAllocation | null> {
    try {
        const snapshot = await getDocs(query(
            collection(db, 'collaborators_private', collaboratorId, 'allocations'),
            orderBy('startDate', 'desc')
        ));

        return pickEvaluationResultAllocation(snapshot.docs.map((allocationDoc) => ({
            id: allocationDoc.id,
            ...allocationDoc.data(),
        })));
    } catch (error) {
        console.warn('Não foi possível carregar alocações do colaborador para o resultado da avaliação.', error);
        return null;
    }
}

async function getExpectedThresholdsForCollaborator(
    collaborator: EvaluationResultCollaborator
): Promise<EvaluationAxisThresholds | null> {
    try {
        const config = await getEvaluationThresholdConfig(collaborator.positionId, collaborator.seniorityId);
        return config?.thresholds ?? null;
    } catch (error) {
        console.warn('Não foi possível carregar parâmetros esperados da avaliação.', error);
        return null;
    }
}

function normalizeExpectedThresholdConfig(config: EvaluationThresholdConfig): EvaluationExpectedSeniorityComparison {
    return {
        seniorityId: config.seniorityId,
        seniorityName: config.seniorityName,
        thresholds: config.thresholds,
    };
}

function buildLegacyExpectedComparisons(): EvaluationExpectedSeniorityComparison[] {
    return [
        {
            seniorityId: 'legacy-junior',
            seniorityName: 'Júnior',
            thresholds: {
                AGIR: expectedByGroupAndSeniority.AGIR.junior,
                CUIDAR: expectedByGroupAndSeniority.CUIDAR.junior,
                INOVAR: expectedByGroupAndSeniority.INOVAR.junior,
                INFLUENCIAR: expectedByGroupAndSeniority.INFLUENCIAR.junior,
            },
        },
        {
            seniorityId: 'legacy-pleno',
            seniorityName: 'Pleno',
            thresholds: {
                AGIR: expectedByGroupAndSeniority.AGIR.pleno,
                CUIDAR: expectedByGroupAndSeniority.CUIDAR.pleno,
                INOVAR: expectedByGroupAndSeniority.INOVAR.pleno,
                INFLUENCIAR: expectedByGroupAndSeniority.INFLUENCIAR.pleno,
            },
        },
        {
            seniorityId: 'legacy-senior',
            seniorityName: 'Sênior',
            thresholds: {
                AGIR: expectedByGroupAndSeniority.AGIR.senior,
                CUIDAR: expectedByGroupAndSeniority.CUIDAR.senior,
                INOVAR: expectedByGroupAndSeniority.INOVAR.senior,
                INFLUENCIAR: expectedByGroupAndSeniority.INFLUENCIAR.senior,
            },
        },
    ];
}

async function getExpectedComparisonsForCollaborator(
    collaborator: EvaluationResultCollaborator
): Promise<EvaluationExpectedSeniorityComparison[]> {
    if (!collaborator.positionId) {
        return buildLegacyExpectedComparisons();
    }

    try {
        const configs = await listEvaluationThresholdConfigs();
        const positionConfigs = configs
            .filter((config) => config.positionId === collaborator.positionId)
            .map(normalizeExpectedThresholdConfig);

        return positionConfigs.length > 0
            ? positionConfigs
            : buildLegacyExpectedComparisons();
    } catch (error) {
        console.warn('Não foi possível carregar comparativo de senioridades da avaliação.', error);
        return buildLegacyExpectedComparisons();
    }
}

function mergeCollaboratorProfile(
    collaborator: EvaluationCollaborator,
    profilesById: Map<string, Record<string, unknown>>,
    seniorityNameById: ReadonlyMap<string, string>,
    allocationDetails?: EvaluationResultAllocation | null
): EvaluationResultCollaborator {
    const collaboratorWithDetails = collaborator as EvaluationResultCollaborator;
    const collaboratorProfile = profilesById.get(collaborator.collaboratorId);
    const responsibleProfile = collaborator.responsibleId
        ? profilesById.get(collaborator.responsibleId)
        : undefined;
    const collaboratorResources = readResourceList(collaboratorWithDetails.resources);
    const profileResources = readResourceList(collaboratorProfile?.resources);
    const profileAllocation = pickEvaluationResultAllocation(readAllocationList((collaboratorProfile?.allocationSummary as { active?: unknown } | undefined)?.active));
    const allocation = allocationDetails ?? profileAllocation;
    const primaryResource = getPrimaryResource(collaboratorResources) ?? getPrimaryResource(profileResources);

    return {
        ...collaborator,
        collaboratorPhotoUrl: collaborator.collaboratorPhotoUrl ?? (collaboratorProfile?.photoUrl as string | null | undefined) ?? null,
        responsiblePhotoUrl: collaborator.responsiblePhotoUrl ?? (responsibleProfile?.photoUrl as string | null | undefined) ?? null,
        positionId: readString(collaboratorWithDetails.positionId) ?? readString(collaboratorProfile?.positionId),
        positionName: readString(collaboratorWithDetails.positionName) ?? readString(collaboratorProfile?.positionName),
        seniorityId: readString(collaboratorWithDetails.seniorityId)
            ?? readString(collaboratorProfile?.seniorityId)
            ?? readString(primaryResource?.seniorityId),
        seniorityName: resolveEvaluationCollaboratorSeniorityName({
            collaborator: collaboratorWithDetails,
            profile: collaboratorProfile,
            seniorityNameById,
        }),
        departmentName: readString(collaboratorWithDetails.departmentName) ?? readString(collaboratorProfile?.departmentName) ?? readString(allocation?.departmentName),
        clientName: readString(collaboratorWithDetails.clientName) ?? readString(collaboratorProfile?.clientName) ?? readString(allocation?.clientName),
        projectName: readString(collaboratorWithDetails.projectName) ?? readString(collaboratorProfile?.projectName) ?? readString(allocation?.projectName),
        resources: collaboratorResources.length > 0 ? collaboratorResources : profileResources,
    };
}

export async function getEvaluationCollaboratorResult(
    campaignId: string,
    collaboratorId: string
): Promise<EvaluationCollaboratorResult | null> {
    const [campaign, collaborators, responsesSnapshot] = await Promise.all([
        getPerformanceEvaluationCampaignById(campaignId),
        getPerformanceEvaluationCollaborators(campaignId),
        getDocs(query(
            collection(db, RESPONSE_COLLECTION),
            where('campaignId', '==', campaignId),
            where('status', '==', 'completed')
        )),
    ]);

    if (!campaign) {
        return null;
    }

    const collaborator = collaborators.find((item) => item.collaboratorId === collaboratorId);
    if (!collaborator) {
        return null;
    }

    const profilesById = await getPublicCollaboratorProfiles([
        collaborator.collaboratorId,
        collaborator.responsibleId ?? '',
    ]);
    const collaboratorProfile = profilesById.get(collaborator.collaboratorId);
    const seniorityNameById = await getSeniorityNamesById(collectSeniorityIds(
        collaborator as EvaluationResultCollaborator,
        collaboratorProfile
    ));
    const allocationDetails = await getCollaboratorAllocationDetails(collaborator.collaboratorId);
    const resultCollaborator = mergeCollaboratorProfile(collaborator, profilesById, seniorityNameById, allocationDetails);
    const [expectedThresholds, expectedComparisons] = await Promise.all([
        getExpectedThresholdsForCollaborator(resultCollaborator),
        getExpectedComparisonsForCollaborator(resultCollaborator),
    ]);
    const responses = responsesSnapshot.docs
        .map((responseDoc) => ({
            id: responseDoc.id,
            ...responseDoc.data(),
        } as PerformanceEvaluationResponse))
        .filter((response) => response.collaboratorId === collaboratorId);

    return buildEvaluationCollaboratorResult({
        campaign,
        collaborator: resultCollaborator,
        responses,
        expectedThresholds,
        expectedComparisons,
    });
}
